import { DocumentTextIcon } from '@heroicons/react/20/solid';
import { message, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store';
import { menuApi } from '../../../../api/menu';
import { setFilesRead } from '../../../../features/cart/cart';
import { TTMenu } from '../../../../interfaces/menus';
import { RootState } from '../../../../rootReducer';
import { LoadingIcon } from '../../../components/Spinner';

type Props = {
  menu: TTMenu;
  loading_menu: boolean;
  from_menu_item?: boolean;
  onFileViewed?: () => void;
};

export function MenuFileView({
  menu,
  loading_menu,
  from_menu_item = false,
  onFileViewed,
}: Props) {
  const [showFile, setShowFile] = useState(from_menu_item);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [fetchingFile, setFetchingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const dispatch = useAppDispatch();

  const files_read = useSelector<RootState, { [key: string]: boolean }>(
    state => state.cart.files_read,
  );

  const isFileRead = menu?.file?.unique_id
    ? files_read[menu.file.unique_id]
    : false;

  const fetchSignedUrl = async () => {
    if (!menu?.file_id) {
      setFileError('No file available to view');
      setFetchingFile(false);
      return;
    }

    try {
      setFetchingFile(true);
      setFileError(null);

      const response = await menuApi.getSignedUrlForFile(menu.file_id);

      if (response.data) {
        setSignedUrl(response.data);
      } else {
        setFileError('Could not retrieve file URL');
      }
    } catch (error) {
      console.error('Error fetching file:', error);
      setFileError(
        error?.response?.data?.detail?.[0]?.msg || 'Unable to fetch the file',
      );
    } finally {
      setFetchingFile(false);
    }
  };

  const handleConfirmRead = () => {
    if (menu?.file?.unique_id) {
      dispatch(setFilesRead(menu.file.unique_id));

      if (onFileViewed && typeof onFileViewed === 'function') {
        onFileViewed();
      }

      if (!from_menu_item) {
        setShowFile(false);
      }
    }
  };

  // Only fetch the URL when needed
  useEffect(() => {
    if ((showFile || from_menu_item) && !signedUrl && !fetchingFile) {
      fetchSignedUrl();
    }
  }, [showFile, from_menu_item, signedUrl, fetchingFile]);

  // Update iframe when URL is available
  useEffect(() => {
    if (iframeRef.current && signedUrl) {
      console.log(signedUrl);
      iframeRef.current.src = signedUrl;
    }
  }, [signedUrl]);

  return (
    <div className="bg-background px-4 px:mx-8 lg:px-16 xl:px-32">
      <div className="flex-col bg-tertiary-container px-2 py-1 mb-2 md:px-4 md:py-2 rounded-md shadow-md">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-1">
            <DocumentTextIcon className="w-4 h-4 text-on-tertiary-container" />
            <h4 className="text-md md:text-md text-on-tertiary-container mb-0">
              {menu?.file?.name || 'Document'}
            </h4>
          </div>
          <div>
            {!from_menu_item && (
              <button
                className="flex rounded-full px-2 py-1 bg-tertiary text-sm text-on-tertiary"
                onClick={() => setShowFile(prev => !prev)}
                aria-expanded={showFile}
                aria-controls="file-view-container"
              >
                {showFile ? 'Hide File' : 'View File'}
              </button>
            )}
          </div>
        </div>

        {(showFile || from_menu_item) && (
          <Spin spinning={fetchingFile} indicator={LoadingIcon}>
            <div id="file-view-container" className="mt-4">
              {fileError ? (
                <div className="text-error p-4 text-center border border-error rounded-md">
                  {fileError}
                </div>
              ) : (
                <div className="relative">
                  <iframe
                    ref={iframeRef}
                    title={menu?.file?.name || 'Menu Document'}
                    width="100%"
                    height="500"
                    className="my-2 border-0"
                    allowFullScreen
                    // sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    onError={() => setFileError('Failed to load document')}
                  />
                </div>
              )}

              {menu?.file_required_to_view && (
                <div className="mt-4 border-t pt-4">
                  <div className="relative flex items-start">
                    <div className="flex h-5 items-center">
                      <input
                        id="read-confirmation"
                        name="read-confirmation"
                        type="checkbox"
                        disabled={isFileRead}
                        checked={isFileRead}
                        onChange={() => handleConfirmRead()}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 
                          disabled:text-indigo-600/50 disabled:cursor-not-allowed 
                          focus:ring-indigo-500"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label
                        htmlFor="read-confirmation"
                        className="font-medium text-gray-700"
                      >
                        I confirm
                      </label>
                      <span className="text-gray-500 ml-1">
                        that I have viewed & read the file.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Spin>
        )}
      </div>
    </div>
  );
}
